/**
 * Safe-area insets (notch / Dynamic Island, home indicator, Android cutouts) for the full-screen
 * app build. The app page sets `viewport-fit=cover`, so the board fills the whole screen; board
 * layouts are computed inside the inset rect and shifted back into board space. Sky and sea
 * still paint full-bleed under the notch while chrome and play stay clear of it.
 *
 * Web and portal builds never set `viewport-fit=cover`, so `env(safe-area-inset-*)` is 0 there
 * and layouts are unchanged. Dev: `?safeArea=59,0,34,0` (top,right,bottom,left) fakes insets.
 */

var ZERO = Object.freeze({ top: 0, right: 0, bottom: 0, left: 0 });

var probeEl = null;
var cachedKey = null;
var cachedInsets = ZERO;

function sidePx(v) {
  var n = Math.floor(Number(v));
  return isFinite(n) && n > 0 ? n : 0;
}

/** @returns {{ top: number, right: number, bottom: number, left: number }} */
export function normalizeSafeAreaInsets(insets) {
  if (!insets) return ZERO;
  return {
    top: sidePx(insets.top),
    right: sidePx(insets.right),
    bottom: sidePx(insets.bottom),
    left: sidePx(insets.left),
  };
}

export function hasSafeAreaInsets(insets) {
  return !!(insets && (insets.top > 0 || insets.right > 0 || insets.bottom > 0 || insets.left > 0));
}

/**
 * Parse the dev override `safeArea=top,right,bottom,left` from a query string.
 * @param {string} search e.g. `location.search`
 */
export function parseSafeAreaOverride(search) {
  if (!search || typeof URLSearchParams === "undefined") return null;
  var raw = new URLSearchParams(search).get("safeArea");
  if (!raw) return null;
  var parts = raw.split(",").map(function (p) { return Number(p.trim()); });
  if (parts.length !== 4 || parts.some(function (n) { return !isFinite(n); })) return null;
  return normalizeSafeAreaInsets({ top: parts[0], right: parts[1], bottom: parts[2], left: parts[3] });
}

function readEnvInsets() {
  if (typeof document === "undefined" || !document.documentElement
    || typeof getComputedStyle !== "function") {
    return ZERO;
  }
  if (!probeEl) {
    probeEl = document.createElement("div");
    probeEl.setAttribute("aria-hidden", "true");
    probeEl.style.cssText = "position:fixed;left:0;top:0;width:0;height:0;visibility:hidden;"
      + "pointer-events:none;padding:env(safe-area-inset-top,0px) env(safe-area-inset-right,0px) "
      + "env(safe-area-inset-bottom,0px) env(safe-area-inset-left,0px);";
    document.documentElement.appendChild(probeEl);
  }
  var cs = getComputedStyle(probeEl);
  return normalizeSafeAreaInsets({
    top: parseFloat(cs.paddingTop),
    right: parseFloat(cs.paddingRight),
    bottom: parseFloat(cs.paddingBottom),
    left: parseFloat(cs.paddingLeft),
  });
}

/**
 * Current insets in CSS px. Cached per viewport size (insets only change with rotation / resize).
 */
export function readSafeAreaInsets() {
  if (typeof window === "undefined") return ZERO;
  var key = window.innerWidth + "x" + window.innerHeight;
  if (key === cachedKey) return cachedInsets;
  cachedKey = key;
  var override = import.meta.env && import.meta.env.DEV && window.location
    ? parseSafeAreaOverride(window.location.search)
    : null;
  cachedInsets = override || readEnvInsets();
  return cachedInsets;
}

/** Test hook. */
export function __resetSafeAreaCache() {
  cachedKey = null;
  cachedInsets = ZERO;
}

export function insetViewport(viewport, insets) {
  return {
    width: Math.max(0, (viewport.width || 0) - insets.left - insets.right),
    height: Math.max(0, (viewport.height || 0) - insets.top - insets.bottom),
  };
}

export function withoutSafeAreaInsets(opts) {
  var out = {};
  for (var k in opts) {
    if (Object.prototype.hasOwnProperty.call(opts, k)) out[k] = opts[k];
  }
  out.safeAreaInsets = null;
  return out;
}

function isRect(v) {
  return !!(v && typeof v === "object" && typeof v.x === "number" && typeof v.y === "number"
    && typeof v.width === "number" && typeof v.height === "number");
}

/**
 * Shift a board computed inside the safe rect back into full-board coordinates: every rect moves
 * by (left, top), the board grows by the insets, and scenery margins absorb them.
 * @template T
 * @param {T} board from computeResponsiveBoard / computeVerticalBoard on the inset viewport
 * @param {{ top: number, right: number, bottom: number, left: number }} insets
 * @returns {T}
 */
export function offsetBoardForSafeArea(board, insets) {
  var out = {};
  for (var key in board) {
    if (!Object.prototype.hasOwnProperty.call(board, key)) continue;
    var v = board[key];
    out[key] = isRect(v)
      ? { x: v.x + insets.left, y: v.y + insets.top, width: v.width, height: v.height }
      : v;
  }
  out.boardWidth = board.boardWidth + insets.left + insets.right;
  out.boardHeight = board.boardHeight + insets.top + insets.bottom;
  var sm = board.sceneryMargins || ZERO;
  out.sceneryMargins = {
    top: sm.top + insets.top,
    right: sm.right + insets.right,
    bottom: sm.bottom + insets.bottom,
    left: sm.left + insets.left,
  };
  out.safeAreaInsets = { top: insets.top, right: insets.right, bottom: insets.bottom, left: insets.left };
  return out;
}
